export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RefreshResult = { accessToken: string; email: string; isAdmin: boolean };
let getToken: (() => string | null) | null = null;
let onRefresh: ((result: RefreshResult) => void) | null = null;
let onAuthFailure: (() => void) | null = null;
let refreshPromise: Promise<boolean> | null = null;

export function configureApiClient(options: {
  getToken: () => string | null;
  onRefresh: (result: RefreshResult) => void;
  onAuthFailure: () => void;
}) {
  getToken = options.getToken;
  onRefresh = options.onRefresh;
  onAuthFailure = options.onAuthFailure;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  mayRefresh = true,
): Promise<T> {
  const token = getToken?.();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: "same-origin",
  });
  if (res.status === 401 && mayRefresh && !path.startsWith("/auth/")) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return request<T>(path, init, false);
    onAuthFailure?.();
  }
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let message = body || res.statusText;
    try {
      const parsed = JSON.parse(body) as { error?: string; message?: string };
      message = parsed.error ?? parsed.message ?? message;
    } catch {
      if (body.trimStart().startsWith("<")) {
        message =
          "Der Server konnte die Anfrage nicht verarbeiten. Bitte versuche es erneut.";
      }
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) return false;
      const result = await response.json() as RefreshResult;
      if (!result.accessToken) return false;
      onRefresh?.(result);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function download(path: string, mayRefresh = true): Promise<Blob> {
  const token = getToken?.();
  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 401 && mayRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return download(path, false);
    onAuthFailure?.();
  }
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    let message = response.statusText;
    try {
      message = (JSON.parse(body) as { error?: string }).error ?? message;
    } catch {
      if (body && !body.trimStart().startsWith("<")) message = body;
    }
    throw new ApiError(response.status, message);
  }
  return response.blob();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  download,
};
